import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Target, Eye, Heart, Shield, Award, Users, Leaf, CheckCircle2, ArrowRight, Phone } from 'lucide-react';
import PageSEO from '../components/PageSEO';

const aboutSEO = {
  title: 'About Lidav Enterprises | NEMA-Licensed Facilities Management Company – Nairobi, Kenya',
  description: 'Learn about Lidav Enterprises Limited — Kenya\'s trusted NEMA-certified partner for environmental services and facilities management since 2013. 500+ clients. 15+ years of excellence across Nairobi and Kenya.',
  canonical: '/about',
  schema: {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About Lidav Enterprises Limited',
    url: 'https://lidaventerprises.com/about',
    description: 'Lidav Enterprises Limited — NEMA-certified facilities management and environmental services company serving Kenya since 2013.',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://lidaventerprises.com' },
        { '@type': 'ListItem', position: 2, name: 'About Us', item: 'https://lidaventerprises.com/about' },
      ],
    },
    mainEntity: {
      '@type': 'Organization',
      name: 'Lidav Enterprises Limited',
      foundingDate: '2013',
      description: 'NEMA-licensed Kenyan company providing professional fumigation, pest control, sanitary disposal, hazardous waste and landscaping services.',
      numberOfEmployees: { '@type': 'QuantitativeValue', minValue: 15, maxValue: 50 },
      award: 'NEMA Certified Environmental Services Provider',
    },
  },
};

const fadeUp = (delay = 0) => ({
  initial:     { opacity: 0, y: 36, scale: 0.98 },
  whileInView: { opacity: 1, y: 0,  scale: 1    },
  viewport:    { once: true, margin: '-80px'     },
  transition:  { duration: 0.6, ease: 'easeOut', delay },
});
const stagger = {
  hidden: {},
  show:   { transition: { staggerChildren: 0.1, delayChildren: 0.1 } },
};
const staggerItem = {
  hidden: { opacity: 0, y: 28, scale: 0.97 },
  show:   { opacity: 1, y: 0,  scale: 1, transition: { duration: 0.52, ease: 'easeOut' } },
};

const values = [
  { icon: Shield,       color: 'bg-blue-500',        gradient: 'from-blue-400 to-blue-600',    title: 'Integrity',      desc: 'We operate with complete transparency, honesty and accountability in every engagement.' },
  { icon: Award,        color: 'bg-brand-red',        gradient: 'from-red-500 to-red-700',      title: 'Excellence',     desc: 'We set the highest standards and continuously improve our services and processes.' },
  { icon: Leaf,         color: 'bg-emerald-500',      gradient: 'from-emerald-400 to-green-600',title: 'Sustainability', desc: 'We use eco-friendly methods and chemicals that protect both people and the environment.' },
  { icon: Users,        color: 'bg-brand-blue',       gradient: 'from-brand-blue to-blue-600',  title: 'People-First',   desc: 'Our clients and team members are our greatest assets — we invest in both.' },
  { icon: Heart,        color: 'bg-rose-500',         gradient: 'from-rose-400 to-rose-600',    title: 'Community',      desc: 'We actively support the communities we serve through responsible business practices.' },
  { icon: CheckCircle2, color: 'bg-orange-500',       gradient: 'from-orange-400 to-orange-600',title: 'Reliability',    desc: '24/7 availability and consistent on-time delivery — you can always count on.' },
];

const team = [
  { name: 'Lilian David',   role: 'Chief Executive Officer', img: '/images/lilian.png', showImg: true,  bio: 'Founder and CEO with 15+ years driving excellence in facilities management across Kenya.' },
  { name: 'Purity Mwende',  role: 'Operations Director',     img: null,                  showImg: false, bio: 'Expert in environmental compliance and waste management systems.' },
  { name: 'Macharia Valve', role: 'Head of Pest Control',   img: null,                  showImg: false, bio: 'Certified entomologist with 15 years of field experience.' },
  { name: 'Emmanuel Mukiri',role: 'Finance Director',        img: null,                  showImg: false, bio: 'Certified financial professional overseeing financial operations and corporate strategy.' },
];

const certifications = [
  { title: 'NEMA Licensed',  sub: 'National Environment Management Authority', icon: Shield },
  { title: 'OSHA Compliant', sub: 'Occupational Safety & Health',              icon: Heart  },
];

export default function About() {
  return (
    <main>
      <PageSEO {...aboutSEO} />
      {/* ── Page Hero ── */}
      <section className="relative min-h-[52vh] flex items-center overflow-hidden bg-brand-blue">
        <div className="absolute inset-0">
          <img src="/images/3.jpg" alt="Lidav team" className="w-full h-full object-cover object-top opacity-12" fetchpriority="high" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-brand-blue via-brand-blue/90 to-brand-blue/70" />
        {/* Dot grid */}
        <div className="absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: 'radial-gradient(circle, #000 1px, transparent 0)', backgroundSize: '28px 28px' }} />
        {/* Blobs */}
        <div className="pointer-events-none absolute -top-24 right-0 w-80 h-80 bg-brand-red/6 rounded-full blur-3xl animate-blob" />
        <div className="pointer-events-none absolute bottom-0 left-1/4 w-72 h-72 bg-white/5 rounded-full blur-3xl animate-blob" style={{ animationDelay: '3s' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-28 pb-16">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            {/* Glass breadcrumb */}
            <div
              className="inline-flex items-center gap-2 text-gray-700 text-xs font-inter mb-5 px-3 py-1.5 rounded-full border"
              style={{
                background:           'rgba(0,0,0,0.07)',
                backdropFilter:       'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                borderColor:          'rgba(0,0,0,0.08)',
              }}
            >
              <Link to="/" className="hover:text-gray-900 transition-colors">Home</Link>
              <span>/</span>
              <span className="text-gray-900 font-semibold">About Us</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black font-poppins text-gray-900 mb-4">
              About <span className="text-brand-red">Lidav</span> Enterprises LTD
            </h1>
            <p className="text-gray-700 text-lg font-inter max-w-xl font-medium leading-relaxed">
              Kenya's leading facilities management and environmental services company — protecting health, environment and spaces since 2013.
            </p>
          </motion.div>
        </div>
        {/* Diagonal bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-white" style={{ clipPath: 'polygon(0 100%, 100% 0, 100% 100%)' }} />
      </section>

      {/* ── Company Story ── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <img src="/images/3.jpg"           alt="Lidav pest control professional" className="rounded-2xl w-full h-36 sm:h-48 md:h-64 object-cover object-top shadow-lg" loading="lazy" />
                <img src="/images/2.jpg"           alt="Lidav fumigation services"       className="rounded-2xl w-full h-36 sm:h-48 md:h-64 object-cover object-center shadow-lg mt-5 sm:mt-8" loading="lazy" />
                <img src="/images/1.jpg"           alt="Lidav pest control in action"   className="rounded-2xl w-full h-36 sm:h-48 md:h-64 object-cover object-center shadow-lg -mt-2 sm:-mt-4" loading="lazy" />
                <img src="/images/landscaping.png" alt="Lidav landscaping team"          className="rounded-2xl w-full h-36 sm:h-48 md:h-64 object-cover object-center shadow-lg mt-2 sm:mt-4" loading="lazy" style={{ objectPosition: 'center 18%' }} />
              </div>
              {/* Experience badge */}
              <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-2xl p-5 text-center"
                style={{
                  background:           'rgba(2,174,242,0.95)',
                  backdropFilter:       'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  boxShadow:            '0 16px 48px rgba(2,174,242,0.30)',
                  border:               '1px solid rgba(255,255,255,0.2)',
                }}
              >
                <div className="text-4xl font-black font-poppins text-brand-red">15+</div>
                <div className="text-xs text-white/80 uppercase tracking-wider font-inter font-bold mt-1">Years of Excellence</div>
              </div>
            </motion.div>

            <div>
              <motion.div {...fadeUp()}>
                <span className="section-tag">Our Story</span>
                <h2 className="text-3xl sm:text-4xl font-bold font-poppins text-gray-900 mt-2 mb-5 leading-tight">
                  Built on a Foundation of <span className="text-gradient-brand">Excellence &amp; Trust</span>
                </h2>
                <div className="space-y-4 text-gray-600 font-inter leading-relaxed">
                  <p>
                    Founded in 2013, Lidav Enterprises Limited began as a waste management company dealing with sanitary waste bin, hazard and pest control in Nairobi with a singular mission: to provide professional, reliable and eco-friendly environmental services that genuinely protect people and spaces.
                  </p>
                  <p>
                    Over the years, we grew into a full-service facilities management company, expanding our expertise to encompass waste management, comprehensive cleaning, office catering, landscaping, and sanitary disposal services — all under one roof.
                  </p>
                  <p>
                    Today, we proudly serve over 500 clients including leading hotels, hospitals, corporate offices, residential estates and government institutions across Nairobi and beyond.
                  </p>
                </div>
              </motion.div>

              <motion.div
                variants={stagger}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="grid grid-cols-2 gap-4 mt-8"
              >
                {[
                  { val: '500+',  label: 'Happy Clients'  },
                  { val: '2000+', label: 'Projects Done'  },
                  { val: '100+',  label: 'Expert Staff'   },
                  { val: '6',     label: 'Service Lines'  },
                ].map(({ val, label }) => (
                  <motion.div
                    key={label}
                    variants={staggerItem}
                    className="rounded-xl p-4 text-center border transition-all duration-250 cursor-default"
                    style={{ background: 'rgba(240,247,255,1)', borderColor: 'rgba(2,174,242,0.12)' }}
                  >
                    <div className="text-2xl font-black font-poppins text-brand-blue">{val}</div>
                    <div className="text-gray-500 text-xs font-inter mt-1">{label}</div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Mission / Vision / Purpose ── */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="pointer-events-none absolute top-20 left-0 w-96 h-96 bg-brand-blue/4 rounded-full blur-3xl" />
        <div className="pointer-events-none absolute bottom-20 right-0 w-96 h-96 bg-brand-red/4 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <motion.div {...fadeUp()} className="text-center mb-14">
            <span className="section-tag">Our Direction</span>
            <h2 className="text-3xl sm:text-4xl font-bold font-poppins text-gray-900 mt-2">
              Mission, Vision &amp; <span className="text-gradient-brand">Purpose</span>
            </h2>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-8"
          >
            {[
              {
                icon: Target, gradient: 'from-brand-blue to-blue-600',
                accentColor: '#02AEF2',
                title: 'Our Mission',
                text: 'To deliver world-class facilities management and environmental services that protect health, preserve the environment, and create clean, safe spaces — with integrity and professionalism.',
              },
              {
                icon: Eye, gradient: 'from-brand-red to-red-700',
                accentColor: '#E30613',
                title: 'Our Vision',
                text: "To be East Africa's most respected and innovative environmental services company, setting the benchmark for sustainability, quality and client satisfaction in the facilities management industry.",
              },
              {
                icon: Heart, gradient: 'from-rose-500 to-rose-700',
                accentColor: '#f43f5e',
                title: 'Our Purpose',
                text: 'We exist to make every space where people live, work and play cleaner, healthier and safer — contributing to a better environment for current and future generations across Kenya.',
              },
            ].map(({ icon: Icon, gradient, accentColor, title, text }) => (
              <motion.div
                key={title}
                variants={staggerItem}
                className="relative rounded-2xl p-8 overflow-hidden group cursor-default transition-all duration-300 hover:-translate-y-1"
                style={{
                  background:           'rgba(255,255,255,0.90)',
                  backdropFilter:       'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  border:               `1px solid rgba(${accentColor === '#02AEF2' ? '2,174,242' : accentColor === '#E30613' ? '227,6,19' : '244,63,94'},0.12)`,
                  boxShadow:            '0 4px 24px rgba(0,0,0,0.05)',
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 20px 56px rgba(0,0,0,0.10)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 4px 24px rgba(0,0,0,0.05)'; }}
              >
                {/* Animated top bar */}
                <div className={`absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r ${gradient} transform scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500`} />

                <div className={`w-14 h-14 bg-gradient-to-br ${gradient} rounded-2xl flex items-center justify-center mb-6 shadow-md group-hover:scale-110 transition-transform duration-300`}>
                  <Icon size={22} className="text-white" />
                </div>
                <h3 className="text-xl font-bold font-poppins text-gray-900 mb-3">{title}</h3>
                <p className="text-gray-600 font-inter text-sm leading-relaxed">{text}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Core Values ── */}
      <section className="py-24 bg-[#f0f7ff] relative overflow-hidden">
        <div className="pointer-events-none absolute top-0 right-1/4 w-80 h-80 bg-brand-red/5 rounded-full blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 left-1/4 w-80 h-80 bg-brand-blue/5 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <motion.div {...fadeUp()} className="text-center mb-16">
            <span className="section-tag">What Drives Us</span>
            <h2 className="text-3xl sm:text-4xl font-bold font-poppins text-gray-900 mt-2">
              Our Core <span className="text-gradient-brand">Values</span>
            </h2>
            <p className="text-gray-500 font-inter mt-4 max-w-2xl mx-auto">
              Every decision we make and every service we provide is guided by these core principles that define who we are.
            </p>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {values.map(({ icon: Icon, color, gradient, title, desc }) => (
              <motion.div
                key={title}
                variants={staggerItem}
                className="group relative rounded-2xl p-7 transition-all duration-300 cursor-default hover:-translate-y-1"
                style={{
                  background:           'rgba(255,255,255,0.88)',
                  backdropFilter:       'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  border:               '1px solid rgba(255,255,255,0.65)',
                  boxShadow:            '0 4px 20px rgba(2,174,242,0.06)',
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 16px 48px rgba(2,174,242,0.14)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 4px 20px rgba(2,174,242,0.06)'; }}
              >
                {/* Top gradient line */}
                <div className={`absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r ${gradient} transform scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500 rounded-t-2xl`} />

                <div className="flex items-start gap-4">
                  <div className={`${color} rounded-xl p-3 flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-sm`}>
                    <Icon size={20} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold font-poppins text-gray-900 mb-2 group-hover:text-brand-blue transition-colors text-base">{title}</h3>
                    <p className="text-gray-600 text-sm font-inter leading-relaxed">{desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Team ── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div {...fadeUp()} className="text-center mb-14">
            <span className="section-tag">The People Behind Lidav</span>
            <h2 className="text-3xl sm:text-4xl font-bold font-poppins text-gray-900 mt-2">
              Meet Our <span className="text-gradient-brand">Leadership Team</span>
            </h2>
            <p className="text-gray-500 font-inter text-base mt-4 max-w-xl mx-auto">
              Experienced professionals dedicated to delivering excellence in every project.
            </p>
          </motion.div>
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {team.map(({ name, role, img, showImg, bio }) => (
              <motion.div
                key={name}
                variants={staggerItem}
                className="rounded-3xl overflow-hidden card-lift group cursor-default"
                style={{
                  background:           'rgba(255,255,255,0.90)',
                  backdropFilter:       'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  border:               '1px solid rgba(226,232,240,0.8)',
                  boxShadow:            '0 4px 20px rgba(2,174,242,0.07)',
                }}
              >
                {showImg ? (
                  <div className="relative overflow-hidden h-80">
                    <img
                      src={img}
                      alt={name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      style={{ objectPosition: 'center 8%' }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                      <p className="text-white text-xs font-inter leading-relaxed">{bio}</p>
                    </div>
                  </div>
                ) : (
                  <div
                    className="relative h-64 flex flex-col items-center justify-center gap-3"
                    style={{ background: 'linear-gradient(135deg, rgba(2,174,242,0.08), rgba(2,174,242,0.04))' }}
                  >
                    <div
                      className="w-20 h-20 rounded-full flex items-center justify-center text-white font-black font-poppins text-3xl shadow-lg"
                      style={{ background: 'linear-gradient(135deg, #02AEF2, #018bc2)' }}
                    >
                      {name.charAt(0)}
                    </div>
                    <p className="text-gray-600 text-xs font-inter px-6 text-center leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-300 font-medium">{bio}</p>
                  </div>
                )}
                <div className="p-5 border-t border-gray-50">
                  <h3 className="font-bold font-poppins text-gray-900">{name}</h3>
                  <p className="text-gray-500 text-sm font-inter mt-0.5">{role}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Certifications — glass on brand-blue ── */}
      <section className="py-20 bg-brand-blue relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'radial-gradient(circle, #000 1px, transparent 0)', backgroundSize: '28px 28px' }} />
        <div className="pointer-events-none absolute top-0 right-0 w-72 h-72 bg-brand-red/6 rounded-full blur-3xl animate-blob" style={{ animationDelay: '1s' }} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <motion.div {...fadeUp()} className="text-center mb-12">
            <span className="inline-block text-brand-red font-bold tracking-widest uppercase text-xs mb-3">Accreditations</span>
            <h2 className="text-3xl sm:text-4xl font-bold font-poppins text-gray-900">
              Licensed, Certified &amp; Compliant
            </h2>
          </motion.div>
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid sm:grid-cols-2 gap-5 max-w-2xl mx-auto"
          >
            {certifications.map(({ title, sub, icon: Icon }) => (
              <motion.div
                key={title}
                variants={staggerItem}
                className="rounded-2xl p-6 text-center transition-all duration-300 group cursor-default hover:-translate-y-1"
                style={{
                  background:           'rgba(255,255,255,0.10)',
                  backdropFilter:       'blur(14px)',
                  WebkitBackdropFilter: 'blur(14px)',
                  border:               '1px solid rgba(255,255,255,0.18)',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.20)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.10)'; }}
              >
                <div className="w-14 h-14 bg-brand-red rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform shadow-md">
                  <Icon size={22} className="text-white" />
                </div>
                <h3 className="font-bold font-poppins text-white mb-1">{title}</h3>
                <p className="text-white/60 text-xs font-inter">{sub}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 bg-[#f0f7ff] relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-[0.3]"
          style={{ background: 'radial-gradient(ellipse at center, rgba(2,174,242,0.08) 0%, transparent 70%)' }} />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
          <motion.div {...fadeUp()}>
            <h2 className="text-3xl sm:text-4xl font-bold font-poppins text-gray-900 mb-4">
              Ready to work with Kenya's best?
            </h2>
            <p className="text-gray-500 font-inter text-base mb-8">Get a free consultation and customised proposal today.</p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/contact"
                className="flex items-center gap-2 bg-brand-blue text-gray-900 px-7 py-3.5 rounded-full font-semibold font-poppins text-sm hover:bg-brand-blue-dark transition-all hover:-translate-y-0.5 shadow-md cursor-pointer"
              >
                Get Free Quote <ArrowRight size={15} />
              </Link>
              <a
                href="tel:0722148999"
                className="flex items-center gap-2 border-2 border-brand-blue text-brand-blue px-7 py-3.5 rounded-full font-semibold font-poppins text-sm hover:bg-brand-blue hover:text-white transition-all cursor-pointer"
              >
                <Phone size={15} /> Call 0722 148 999
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
